import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../services/adminApi';
import './AdminTemplates.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function AdminTemplates() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
        total: 0,
        totalPages: 0
    });

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit
            };
            if (search) params.search = search;
            if (categoryFilter !== 'all') params.category = categoryFilter;

            const res = await adminApi.getTemplates(params);
            setTemplates(res.data.templates || []);
            setPagination(prev => ({
                ...prev,
                total: res.data.pagination?.total || 0,
                totalPages: res.data.pagination?.total_pages || 0
            }));
        } catch (err) {
            setError('Erreur lors du chargement des templates');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, categoryFilter]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await adminApi.getTemplateCategories();
            setCategories(res.data.categories || []);
        } catch (err) {
            console.error('Erreur chargement categories:', err);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleCategoryFilter = (e) => {
        setCategoryFilter(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleDelete = async (template) => {
        if (!window.confirm(`Voulez-vous vraiment supprimer le template "${template.name}" ?`)) {
            return;
        }

        try {
            await adminApi.deleteTemplate(template.id);
            await loadTemplates();
        } catch (err) {
            alert('Erreur lors de la suppression');
            console.error(err);
        }
    };

    const handleToggleStatus = async (template) => {
        try {
            await adminApi.updateTemplate(template.id, {
                ...template,
                is_active: !template.is_active
            });
            await loadTemplates();
        } catch (err) {
            alert('Erreur lors de la modification du statut');
            console.error(err);
        }
    };

    return (
        <AdminLayout>
            <div className="admin-templates">
                <div className="admin-page-header with-actions">
                    <div>
                        <h1>Bibliotheque de signatures</h1>
                        <p>Templates publics disponibles pour tous les utilisateurs</p>
                    </div>
                    <button className="admin-btn admin-btn-primary" onClick={() => navigate('/admin/templates/new')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Nouveau template
                    </button>
                </div>

                {/* Filters */}
                <div className="admin-filters">
                    <div className="admin-search">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Rechercher un template..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>
                    <select
                        className="admin-select"
                        value={categoryFilter}
                        onChange={handleCategoryFilter}
                    >
                        <option value="all">Toutes les categories</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                    <span className="admin-result-count">
                        {pagination.total} template{pagination.total > 1 ? 's' : ''}
                    </span>
                </div>

                {/* Templates Grid */}
                {loading ? (
                    <div className="admin-loading-inline">Chargement...</div>
                ) : error ? (
                    <div className="admin-error">{error}</div>
                ) : templates.length === 0 ? (
                    <div className="admin-card">
                        <p className="no-data">Aucun template trouve</p>
                    </div>
                ) : (
                    <>
                        <div className="templates-grid">
                            {templates.map(template => (
                                <div key={template.id} className="template-card">
                                    <div className="template-preview">
                                        {template.thumbnail_url ? (
                                            <img src={`${API_URL}${template.thumbnail_url}`} alt={template.name} />
                                        ) : (
                                            <div className="template-placeholder">
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <path d="M3 9h18M9 21V9" />
                                                </svg>
                                            </div>
                                        )}
                                        {!template.is_active && (
                                            <div className="template-inactive-overlay">
                                                <span>Inactif</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="template-info">
                                        <h3 className="template-name">{template.name}</h3>
                                        {template.category_name && (
                                            <span className="template-category">{template.category_name}</span>
                                        )}
                                        {template.description && (
                                            <p className="template-description">{template.description}</p>
                                        )}
                                        {template.tags?.length > 0 && (
                                            <div className="template-tags">
                                                {template.tags.slice(0, 3).map((tag, i) => (
                                                    <span key={i} className="template-tag">{tag}</span>
                                                ))}
                                                {template.tags.length > 3 && (
                                                    <span className="template-tag more">+{template.tags.length - 3}</span>
                                                )}
                                            </div>
                                        )}
                                        <div className="template-stats">
                                            <span className="template-usage">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                                    <circle cx="9" cy="7" r="4" />
                                                </svg>
                                                {template.usage_count || 0} utilisations
                                            </span>
                                        </div>
                                    </div>
                                    <div className="template-actions">
                                        <button
                                            className="template-action-btn"
                                            onClick={() => navigate(`/admin/templates/${template.id}/edit`)}
                                            title="Modifier"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                        <button
                                            className={`template-action-btn ${template.is_active ? 'active' : ''}`}
                                            onClick={() => handleToggleStatus(template)}
                                            title={template.is_active ? 'Desactiver' : 'Activer'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                {template.is_active ? (
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 100 6 3 3 0 000-6z" />
                                                ) : (
                                                    <>
                                                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                                                        <line x1="1" y1="1" x2="23" y2="23" />
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                        <button
                                            className="template-action-btn danger"
                                            onClick={() => handleDelete(template)}
                                            title="Supprimer"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3,6 5,6 21,6" />
                                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="admin-pagination">
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                >
                                    Precedent
                                </button>
                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (pagination.totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (pagination.page <= 3) {
                                        pageNum = i + 1;
                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                        pageNum = pagination.totalPages - 4 + i;
                                    } else {
                                        pageNum = pagination.page - 2 + i;
                                    }
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={pagination.page === pageNum ? 'active' : ''}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </>
                )}

            </div>
        </AdminLayout>
    );
}

export default AdminTemplates;
